import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { ServicesOverviewTableViewOverviewTableView } from './ServicesOverviewTableView';
import {
  useDeleteLineItemConfiguration,
  useDeleteLineItemConfigurationPhoenix,
  useGetTaxLineConfigurations,
  useGetTaxLineConfigurationsPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import { useTrackStructuredEvent } from '@pflegenavi/frontend/tracking';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useErrorSnackbar } from '@pflegenavi/web-components';
import { useGetGlobalServiceProviderId } from '../../hooks/useGetGlobalServiceProviderId';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ServicesOverviewTableTableProps {}

export const ServicesOverviewTableTable: FC<
  ServicesOverviewTableTableProps
> = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  const { globalServiceProviderId } = useGetGlobalServiceProviderId();
  const usePhoenix = useFeatureFlagEnabled(FeatureFlag.dev_ReceiptBatchPhoenix);
  const { data, isLoading } = (
    usePhoenix
      ? useGetTaxLineConfigurationsPhoenix
      : useGetTaxLineConfigurations
  )(globalServiceProviderId);

  const { mutateAsync: deleteTaxLineConfig } = (
    usePhoenix
      ? useDeleteLineItemConfigurationPhoenix
      : useDeleteLineItemConfiguration
  )(globalServiceProviderId);

  const handleDeleteTaxLineConfig = useCallback(
    async (id: string) => {
      try {
        await deleteTaxLineConfig({ id });
        enqueueSnackbar(
          t('service-provider-page.services.delete-modal.success-to-delete'),
          { variant: 'success' }
        );
      } catch (error) {
        enqueueErrorSnackbar(
          error,
          t('service-provider-page.services.delete-modal.failed-to-delete')
        );
      }
    },
    [deleteTaxLineConfig, enqueueErrorSnackbar, enqueueSnackbar, t]
  );

  return (
    <ServicesOverviewTableViewOverviewTableView
      data={data ?? []}
      page={page}
      pageSize={pageSize}
      onChangePage={setPage}
      onChangePageSize={setPageSize}
      isLoading={isLoading}
      handleDeleteTaxLineConfig={handleDeleteTaxLineConfig}
      useTrackStructuredEvent={useTrackStructuredEvent}
    />
  );
};
